import {
  UserDto,
  ACTION_SOURCES,
  QUESTION_CATEGORIES_LIST,
  ActionSource,
  ActionStatusType,
  QuestionDifficulty
} from '@domain/pom'
import {
  FilterContrastProvider,
  getButtonOutlineWhite,
  selectFieldFilterSx
} from '@hconnect/common/styles'
import {FilterOption, FilterButton, DateRangePicker, buttonOnBlueSx} from '@hconnect/uikit/src/lib2'
import {defaultQuickSelectionItems} from '@hconnect/uikit/src/lib2/components/simpleDatePicker/dateRangePickerHelpers'
import {Box} from '@mui/material'
import {FilterMultiSelect, FilterSelect, FilterTextField} from '@widgets/common-filters'
import {isBoolean, isEmpty} from 'lodash'
import React, {useState, useMemo, useCallback, ReactNode} from 'react'
import {useTranslation} from 'react-i18next'

import {ResponsiblePersonField} from '../components'
import {CLIENT_SIDE_FILTER_KEYS, FEATURE_NAMESPACE} from '../consts'
import {
  useSections,
  useUserById,
  useTitleFilter,
  useSectionFilter,
  useTopicFilter,
  useAssigneeFilter,
  useQuestionIdFilter,
  useSourceFilter,
  useCostLeadershipFilter,
  useStatusFilter,
  useDueDateRangeParam,
  useDueDateRange,
  useQuestionCategoriesFilter,
  useTimeZone
} from '../hooks'
import {ActionFilterParameters, ACTION_STATUS_LIST} from '../types'
import {getSectionIds, getTopicIds, getSectionById, getQuestionIds} from '../utils'

type OptionKey = keyof ActionFilterParameters

const COST_LEADERSHIP_OPTIONS = ['true', 'false'] as const
type CostLeadershipOption = (typeof COST_LEADERSHIP_OPTIONS)[number]

const costLeadershipToOption = (value?: boolean): CostLeadershipOption | undefined => {
  if (value === undefined) return undefined
  return value ? 'true' : 'false'
}

const optionToCostLeadership = (option?: CostLeadershipOption): boolean | undefined =>
  option ? option === 'true' : undefined

const calcVisibleOptions = (filterParams: ActionFilterParameters): OptionKey[] =>
  Object.keys(filterParams)
    .map((key) => (isBoolean(filterParams[key]) || !isEmpty(filterParams[key]) ? key : undefined))
    .filter(Boolean) as OptionKey[]

const isChecked = (key: OptionKey, options: OptionKey[]): boolean => options.includes(key)

const optionKeys: OptionKey[] = [
  'title',
  'costLeadership',
  'sources',
  'sectionIds',
  'topicIds',
  'questionIds',
  'assignees',
  'dueDateRange',
  'statuses',
  ...CLIENT_SIDE_FILTER_KEYS
]

type FilterBarProps = {
  additionalActions?: ReactNode
  // true when hosted on the legacy blue surface; false (default) on the Suite grey surface
  inverted?: boolean
}

export const FilterBar: React.FC<FilterBarProps> = ({additionalActions, inverted = false}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const timezone = useTimeZone()
  const [title, setTitle] = useTitleFilter()
  const [sectionIds, setSectionIds] = useSectionFilter()
  const [topicIds, setTopicIds] = useTopicFilter()
  const [assignee, setAssignee] = useAssigneeFilter()
  const [timeRange, setTimeRange] = useDueDateRangeParam()
  const dueDateRange = useDueDateRange(timezone)
  const {data: user} = useUserById(assignee)

  const [questionIds, setQuestionIds] = useQuestionIdFilter()
  const [sources, setSources] = useSourceFilter()
  const [costLeadership, setCostLeadership] = useCostLeadershipFilter()
  const [statuses, setStatuses] = useStatusFilter()
  const [questionCategories, setQuestionCategories] = useQuestionCategoriesFilter()
  const {data: sectionData = []} = useSections()
  const sectionDataIds = useMemo(() => getSectionIds(sectionData), [sectionData])
  const topicDataIds = useMemo(
    () => getTopicIds(sectionData, sectionIds),
    [sectionData, sectionIds]
  )
  const questionDataIds = useMemo(
    () => getQuestionIds(sectionData, sectionIds, topicIds),
    [sectionData, sectionIds, topicIds]
  )

  const resetMap: Record<OptionKey, () => void> = useMemo(
    () => ({
      title: () => setTitle(undefined),
      sectionIds: () => setSectionIds(undefined),
      topicIds: () => setTopicIds(undefined),
      assignees: () => setAssignee(undefined),
      questionIds: () => setQuestionIds(undefined),
      sources: () => setSources(undefined),
      costLeadership: () => setCostLeadership(undefined),
      statuses: () => setStatuses(undefined),
      questionCategories: () => setQuestionCategories(undefined),
      dueDateRange: () => {
        setTimeRange(undefined)
      }
    }),
    [
      setAssignee,
      setCostLeadership,
      setQuestionCategories,
      setQuestionIds,
      setSectionIds,
      setSources,
      setStatuses,
      setTimeRange,
      setTitle,
      setTopicIds
    ]
  )

  const setTopicIdsAndResetChildren = useCallback(
    (v: string[] | undefined) => {
      setTopicIds(v)
      setQuestionIds(undefined)
    },
    [setTopicIds, setQuestionIds]
  )

  const setSectionIdsAndResetChildren = useCallback(
    (v: string[] | undefined) => {
      setSectionIds(v)
      setTopicIdsAndResetChildren(undefined)
    },
    [setSectionIds, setTopicIdsAndResetChildren]
  )

  const [visibleOptions, setVisibleOptions] = useState<OptionKey[]>(
    calcVisibleOptions({
      title,
      statuses,
      questionCategories,
      sources,
      costLeadership,
      sectionIds,
      assignees: assignee ? [assignee] : [],
      topicIds,
      questionIds,
      dueDateRange
    })
  )

  const removeFilter = useCallback(
    (key: OptionKey) => {
      resetMap[key]?.()
      setVisibleOptions((options) => options.filter((option) => option !== key))
    },
    [resetMap]
  )

  const toOptions = useCallback(
    <T extends string>(values: ReadonlyArray<T>, toLabel: (value: T) => string = String) =>
      values.map((value) => ({value, label: toLabel(value)})),
    []
  )

  const options: FilterOption<OptionKey>[] = optionKeys.map((optionId) => {
    return {
      id: optionId,
      label: t(`filter.${optionId}`)
    }
  })

  return (
    <FilterContrastProvider isContrastMode={inverted}>
      <Box display={'flex'} justifyContent={'flex-end'} gap={1.5} flexWrap={'wrap'}>
        {isChecked('title', visibleOptions) && (
          <FilterTextField<OptionKey>
            name={'title'}
            label={t('filter.title')}
            value={title}
            onChange={(_, value) => setTitle(value)}
            data-test-id={'filter-control-title'}
          />
        )}
        {isChecked('costLeadership', visibleOptions) && (
          <FilterSelect<CostLeadershipOption, OptionKey>
            name={'costLeadership'}
            label={t('filter.costLeadership')}
            value={costLeadershipToOption(costLeadership)}
            options={toOptions(COST_LEADERSHIP_OPTIONS, (option) =>
              t(`filter.costLeadershipOptions.${option}`)
            )}
            onChange={(_, option) => setCostLeadership(optionToCostLeadership(option))}
            clearFilter={removeFilter}
            data-test-id={'filter-control-cost-leadership'}
          />
        )}
        {isChecked('sources', visibleOptions) && (
          <FilterMultiSelect<ActionSource, OptionKey>
            name={'sources'}
            label={t('filter.sources')}
            value={sources}
            options={toOptions(ACTION_SOURCES, (source) => t(`actionSource.${source}`))}
            onChange={(_, value) => setSources(value)}
            clearFilter={removeFilter}
            renderValue={(selected) =>
              selected.map((source) => t(`actionSource.${source}`)).join(', ')
            }
            data-test-id={'filter-control-source'}
          />
        )}
        {isChecked('sectionIds', visibleOptions) && (
          <FilterMultiSelect<string, OptionKey>
            name={'sectionIds'}
            label={t('filter.sectionIds')}
            value={sectionIds}
            options={toOptions(sectionDataIds, (id) => getSectionById(sectionData, id)?.name ?? '')}
            onChange={(_, value) => setSectionIdsAndResetChildren(value)}
            clearFilter={removeFilter}
            renderValue={(selected) =>
              selected.map((id) => getSectionById(sectionData, id)?.name ?? '').join(', ')
            }
            data-test-id={'filter-control-section'}
          />
        )}
        {isChecked('topicIds', visibleOptions) && (
          <FilterMultiSelect<string, OptionKey>
            name={'topicIds'}
            label={t('filter.topicIds')}
            value={topicIds}
            options={toOptions(topicDataIds)}
            onChange={(_, value) => setTopicIdsAndResetChildren(value)}
            clearFilter={removeFilter}
            data-test-id={'filter-control-topic'}
          />
        )}
        {isChecked('questionIds', visibleOptions) && (
          <FilterMultiSelect<string, OptionKey>
            name={'questionIds'}
            label={t('filter.questionIds')}
            value={questionIds}
            options={toOptions(questionDataIds)}
            onChange={(_, value) => setQuestionIds(value)}
            clearFilter={removeFilter}
            data-test-id={'filter-control-questionId'}
          />
        )}
        {isChecked('assignees', visibleOptions) && (
          <ResponsiblePersonField
            sx={selectFieldFilterSx}
            value={assignee ? user : undefined}
            onChange={(user?: UserDto) => {
              setAssignee(user?.userId)
            }}
          />
        )}
        {isChecked('dueDateRange', visibleOptions) && (
          <DateRangePicker
            onTimeRangeParamChange={setTimeRange}
            timeRangeParam={timeRange}
            singleDateSwitchLabel={t('quickSelection.pickSingleDate')}
            selectionItems={defaultQuickSelectionItems(t, timezone, new Date())}
            timezone={timezone}
            isContrastMode={inverted}
          />
        )}
        {isChecked('statuses', visibleOptions) && (
          <FilterMultiSelect<ActionStatusType, OptionKey>
            name={'statuses'}
            label={t('filter.statuses')}
            value={statuses}
            options={toOptions(ACTION_STATUS_LIST, (status) => t(`actions.${status}`))}
            onChange={(_, value) => setStatuses(value)}
            clearFilter={removeFilter}
            renderValue={(selected) => selected.map((status) => t(`actions.${status}`)).join(', ')}
            data-test-id={'filter-control-status'}
          />
        )}
        {isChecked('questionCategories', visibleOptions) && (
          <FilterMultiSelect<QuestionDifficulty, OptionKey>
            name={'questionCategories'}
            label={t('filter.questionCategories')}
            value={questionCategories}
            options={toOptions(QUESTION_CATEGORIES_LIST, (category) => t(`actions.${category}`))}
            onChange={(_, value) => setQuestionCategories(value)}
            clearFilter={removeFilter}
            renderValue={(selected) =>
              selected.map((category) => t(`actions.${category}`)).join(', ')
            }
            data-test-id={'filter-control-question-category'}
          />
        )}
        <FilterButton
          caption={t('filter.button.caption')}
          options={options}
          selectedIds={visibleOptions}
          sx={inverted ? buttonOnBlueSx : getButtonOutlineWhite}
          onOptionChange={(optionId, checked) => {
            !checked && resetMap[optionId]?.()
            checked && optionId === 'costLeadership' && setCostLeadership(true)
            setVisibleOptions((options) =>
              checked ? [...options, optionId] : options.filter((id) => id !== optionId)
            )
          }}
        />
        {additionalActions}
      </Box>
    </FilterContrastProvider>
  )
}
